import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useCreate, useUpdate, useDelete, useList, useApiUrl, useCustomMutation, useCustom } from "@refinedev/core";
import { resolveApiUrl } from '@/providers/api-url';
import type { PricingTier } from '@/resources/channel-passes/types';
import { SortableList } from "@/components/dnd/SortableList";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Video,
  FileText,
  ClipboardList,
  BookOpen,
  Headphones,
  Loader2,
  Pencil,
  Check,
  AlertCircle,
  CalendarClock,
  Flame,
  Timer,
} from "lucide-react";
import { useConfirm } from "@/hooks/use-confirm";
import { RichTextEditor } from "@/components/editor/RichTextEditor";
import {
  LanguageScrollTabs,
  LanguageFillSummary,
  type Language,
  type LanguageFillInfo,
} from "@/components/translations";
import { ModuleItemType as ModuleItemTypeEnum } from "@masters-club/types";
import {
  getModuleTitle,
  getItemTitle,
  MODULE_ITEM_TYPE_OPTIONS,
  type Module,
  type ModuleItem,
  type ModuleItemType,
  type ModuleItemTranslation,
  type HomeworkType,
  type HomeworkConfig,
} from "./types";
import { ModuleItemMediaSection } from "./ModuleItemMediaSection";
import { ModuleItemAttachments } from "@/resources/module-items/components/module-item-attachments";
import { VideoUpload } from "@/components/upload";
import { LivestreamSourceBadge } from "./LivestreamSourceBadge";
import { FileImageUpload } from "@/components/upload/FileImageUpload";
import { BookSelector } from "@/resources/books/components/BookSelector";
import { ContentTagsField } from "@/components/shared";

const MODULES_EDITOR_API_URL = resolveApiUrl();

interface LanguageFromApi {
  code: string;
  name: string;
  nativeName: string;
  flagEmoji: string;
  flag?: string;
  isDefault: boolean;
}

interface ModulesInlineEditorProps {
  courseId: string;
  modules: Module[];
  onRefresh: () => void;
  addModuleOpen?: boolean;
  onAddModuleOpenChange?: (open: boolean) => void;
  /** Рендер-проп для вывода language tabs в другое место (например, в header) */
  renderLanguageTabs?: (tabs: React.ReactNode) => void;
}

const STORAGE_KEY = "modules-editor-visible-languages";

const getItemIcon = (type: ModuleItemType) => {
  switch (type) {
    case "VIDEO":
      return <Video className="h-4 w-4" />;
    case "AUDIO":
      return <Headphones className="h-4 w-4" />;
    case "ARTICLE":
      return <FileText className="h-4 w-4" />;
    case "HOMEWORK":
      return <ClipboardList className="h-4 w-4" />;
    case "BOOK":
      return <BookOpen className="h-4 w-4" />;
    default:
      return <FileText className="h-4 w-4" />;
  }
};

/**
 * Индикатор заполненности модуля по всем языкам
 */
function ModuleFillIndicator({
  module,
  languages,
}: {
  module: Module;
  languages: Language[];
}) {
  const stats = useMemo(() => {
    let filled = 0;
    const total = languages.length;

    for (const lang of languages) {
      const t = (module.translations ?? []).find((tr) => tr.languageCode === lang.code);
      if (t?.title?.trim()) {filled++;}
    }

    return { filled, total };
  }, [module, languages]);

  if (stats.filled === stats.total) {
    return (
      <div className="flex items-center" title="Модуль переведён на все языки">
        <Check className="h-3.5 w-3.5 text-green-500" />
      </div>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="cursor-pointer">
          <Badge
            variant="secondary"
            className="h-5 px-1.5 text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
          >
            {stats.filled}/{stats.total}
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3">
        <div className="space-y-1">
          <p className="font-medium text-sm">Переводы модуля</p>
          {languages.map((lang) => {
            const t = (module.translations ?? []).find((tr) => tr.languageCode === lang.code);
            const hasTitle = t?.title?.trim();
            return (
              <div key={lang.code} className="flex items-center gap-2 text-xs">
                <span>{lang.flag || lang.flagEmoji}</span>
                <span className="flex-1">{lang.nativeName}</span>
                {hasTitle ? (
                  <Check className="h-3 w-3 text-green-500" />
                ) : (
                  <div className="h-2 w-2 rounded-full bg-muted-foreground/30" />
                )}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Индикатор заполненности урока по всем языкам
 */
function ItemFillIndicator({
  item,
  languages,
}: {
  item: ModuleItem;
  languages: Language[];
}) {
  const stats = useMemo(() => {
    let filled = 0;
    const total = languages.length;

    for (const lang of languages) {
      const t = (item.translations ?? []).find(
        (tr: ModuleItemTranslation) => tr.languageCode === lang.code
      );
      if (t?.title?.trim()) {filled++;}
    }

    return { filled, total };
  }, [item, languages]);

  if (stats.filled === stats.total) {
    return (
      <div className="flex items-center" title="Урок переведён на все языки">
        <Check className="h-3 w-3 text-green-500" />
      </div>
    );
  }

  if (stats.filled === 0) {
    return (
      <div
        className="h-2 w-2 rounded-full bg-muted-foreground/30"
        title="Нет переводов"
      />
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="cursor-pointer">
          <Badge
            variant="secondary"
            className="h-4 px-1 text-[9px] bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
          >
            {stats.filled}/{stats.total}
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3">
        <div className="space-y-1">
          <p className="font-medium text-sm">Переводы урока</p>
          {languages.map((lang) => {
            const t = (item.translations ?? []).find(
              (tr: ModuleItemTranslation) => tr.languageCode === lang.code
            );
            const hasTitle = t?.title?.trim();
            return (
              <div key={lang.code} className="flex items-center gap-2 text-xs">
                <span>{lang.flag || lang.flagEmoji}</span>
                <span className="flex-1">{lang.nativeName}</span>
                {hasTitle ? (
                  <Check className="h-3 w-3 text-green-500" />
                ) : (
                  <div className="h-2 w-2 rounded-full bg-muted-foreground/30" />
                )}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function ModulesInlineEditor({
  courseId,
  modules,
  onRefresh,
  addModuleOpen = false,
  onAddModuleOpenChange,
  renderLanguageTabs: _renderLanguageTabs,
}: ModulesInlineEditorProps) {
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [currentLang, setCurrentLang] = useState<string>("ru");
  const [visibleLanguages, setVisibleLanguages] = useState<string[]>([]);

  // Fetch pricing tiers to determine if isPreview toggle should be shown
  const { query: pricingTiersQuery } = useCustom<PricingTier[]>({
    url: `${MODULES_EDITOR_API_URL}/pricing-tiers/admin`,
    method: 'get',
    config: {
      query: { courseId },
    },
  });
  const hasPricingTiers = (pricingTiersQuery?.data?.data ?? []).length > 0;

  // Fetch languages from API
  const languagesQuery = useList<LanguageFromApi>({
    resource: "languages",
    pagination: { pageSize: 100 },
  });

  const allLanguages: Language[] = useMemo(() => {
    return (languagesQuery.result?.data ?? []).map((l) => ({
      code: l.code,
      name: l.name,
      nativeName: l.nativeName,
      flag: l.flag || l.flagEmoji,
      flagEmoji: l.flagEmoji,
    }));
  }, [languagesQuery.result]);

  // Load saved languages on mount - run only once when languages are loaded
  const languagesLoadedRef = React.useRef(false);
  useEffect(() => {
    if (languagesLoadedRef.current || allLanguages.length === 0) {return;}
    languagesLoadedRef.current = true;

    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setVisibleLanguages(parsed);
          return;
        }
      } catch {
        // ignore
      }
    }
    // Default: show all languages
    setVisibleLanguages(allLanguages.map((l) => l.code));
  }, [allLanguages]);

  // Set default language when languages load - run only once
  const defaultLangSetRef = React.useRef(false);
  useEffect(() => {
    if (defaultLangSetRef.current || allLanguages.length === 0) {return;}

    // Only set default if current language is not in the list
    if (!allLanguages.find((l) => l.code === currentLang)) {
      defaultLangSetRef.current = true;
      const defaultLang = (languagesQuery.result?.data ?? []).find((l) => l.isDefault);
      if (defaultLang) {
        setCurrentLang(defaultLang.code);
      } else if (allLanguages[0]) {
        setCurrentLang(allLanguages[0].code);
      }
    }
  }, [allLanguages, currentLang, languagesQuery.result]);

  const handleVisibleLanguagesChange = useCallback((codes: string[]) => {
    setVisibleLanguages(codes);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(codes));
  }, []);

  // Calculate fill status for each language
  const fillStatus = useMemo((): Record<string, LanguageFillInfo> => {
    const status: Record<string, LanguageFillInfo> = {};
    for (const lang of allLanguages) {
      let filled = 0;
      let total = 0;
      for (const module of modules) {
        total++;
        const t = (module.translations ?? []).find((tr) => tr.languageCode === lang.code);
        if (t?.title?.trim()) {filled++;}
        for (const item of module.items ?? []) {
          total++;
          const it = (item.translations ?? []).find(
            (tr: ModuleItemTranslation) => tr.languageCode === lang.code
          );
          if (it?.title?.trim()) {filled++;}
        }
      }
      status[lang.code] = {
        status: filled === total && total > 0 ? "complete" : (filled > 0 ? "partial" : "empty"),
        filled,
        total,
      };
    }
    return status;
  }, [allLanguages, modules]);

  // Add module state
  const [newModuleTitle, setNewModuleTitle] = useState("");
  const [internalAddModuleOpen, setInternalAddModuleOpen] = useState(false);

  // Add item state
  const [newItemTitle, setNewItemTitle] = useState("");
  const [newItemType, setNewItemType] = useState<ModuleItemType>("VIDEO");
  const [addingItemForModule, setAddingItemForModule] = useState<string | null>(null);

  // Edit item state
  const [editingItem, setEditingItem] = useState<ModuleItem | null>(null);
  const [editItemData, setEditItemData] = useState<{
    translations: Record<string, { title: string; content: string }>;
    videoUrl: string;
    videoDuration: number | null;
    isPreview: boolean;
    type: ModuleItemType;
    homeworkConfig: HomeworkConfig | null;
    bookId: number | null;
    posterUrl: string | null;
    posterFileId: number | null;
    availableFrom: string | null;
  } | null>(null);

  // Edit module state
  const [editingModule, setEditingModule] = useState<Module | null>(null);
  const [editModuleData, setEditModuleData] = useState<{
    translations: Record<string, { title: string; description: string }>;
    availableFrom: string | null;
  } | null>(null);

  // Синхронизировать editingItem с актуальными данными из modules (processing status и т.д.)
  useEffect(() => {
    if (!editingItem) {return;}
    for (const mod of modules) {
      const fresh = mod.items.find((i) => i.id === editingItem.id);
      if (fresh) {
        setEditingItem(fresh);
        return;
      }
    }
  }, [modules]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Автоизвлечение постера для legacy-видео (без posterUrl) ──
  const apiUrl = useApiUrl();

  // Единый per-item стейт автогенерации постера — избавляет от перепутывания
  // состояний между уроками при переключении sheet.
  type PosterExtractState = {
    itemId: string;
    status: 'extracting' | 'failed';
  };
  const [posterState, setPosterState] = useState<PosterExtractState | null>(null);

  const activePosterState =
    posterState && editingItem && posterState.itemId === editingItem.id
      ? posterState
      : null;
  const posterExtracting = activePosterState?.status === 'extracting';
  const posterExtractFailed = activePosterState?.status === 'failed';

  // Set уроков, для которых уже был автотриггер в рамках текущего открытия sheet.
  // Чистится при закрытии sheet (см. эффект ниже).
  const autoTriggeredItemsRef = useRef<Set<string>>(new Set());

  // Ref для отслеживания, какие poster-поля из editingItem мы уже синхронизировали
  // с editItemData. Защищает от отката пользовательской правки постера при onRefresh.
  const syncedPosterRef = useRef<{
    itemId: string;
    posterFileId: number | null;
    posterUrl: string | null;
  } | null>(null);

  const triggerExtractPoster = useCallback(
    async (itemId: string) => {
      setPosterState({ itemId, status: 'extracting' });
      try {
        const response = await fetch(`${apiUrl}/admin/video/${itemId}/extract-poster`, {
          method: 'POST',
        });
        if (!response.ok) {
          setPosterState({ itemId, status: 'failed' });
        }
      } catch {
        setPosterState({ itemId, status: 'failed' });
      }
    },
    [apiUrl],
  );

  // Автотриггер при открытии sheet для VIDEO без постера, но с видео.
  useEffect(() => {
    if (!editingItem || !editItemData) {return;}
    if (editItemData.type !== ModuleItemTypeEnum.VIDEO) {return;}
    if (editItemData.posterUrl !== null || editItemData.posterFileId !== null) {return;}
    const hasVideo = Boolean(editingItem.hlsPlaylistUrl || editingItem.videoUrl);
    if (!hasVideo) {return;}
    if (autoTriggeredItemsRef.current.has(editingItem.id)) {return;}
    autoTriggeredItemsRef.current.add(editingItem.id);
    void triggerExtractPoster(editingItem.id);
  }, [editingItem, editItemData, triggerExtractPoster]);

  // Ждём готовность постера: один отложенный refetch через 8с + финальный таймаут 30с.
  // Вместо дорогого polling'а каждые 2с — бэкенд-воркер обрабатывает постер за 3-8с,
  // один refetch в этом окне покрывает happy-path; на 30с объявляем провал.
  useEffect(() => {
    if (!posterExtracting || !editingItem) {return;}
    const itemId = editingItem.id;
    const refetchTimer = setTimeout(() => {
      onRefresh();
    }, 8_000);
    const failTimer = setTimeout(() => {
      onRefresh();
      setPosterState((prev) =>
        prev && prev.itemId === itemId && prev.status === 'extracting'
          ? { itemId, status: 'failed' }
          : prev,
      );
    }, 30_000);
    return () => {
      clearTimeout(refetchTimer);
      clearTimeout(failTimer);
    };
  }, [posterExtracting, editingItem, onRefresh]);

  // Синхронизация poster-полей из свежего editingItem в editItemData.
  // Срабатывает только если значения в editingItem РЕАЛЬНО изменились между
  // рендерами (через syncedPosterRef) — это защищает от отката пользовательской
  // ручной правки постера, которую он мог сделать в форме после onRefresh.
  useEffect(() => {
    if (!editingItem || !editItemData) {
      return;
    }
    const prev = syncedPosterRef.current;
    const sameItem = prev?.itemId === editingItem.id;
    const posterChanged =
      !sameItem ||
      prev.posterFileId !== (editingItem.posterFileId ?? null) ||
      prev.posterUrl !== (editingItem.posterUrl ?? null);

    if (!posterChanged) {
      return;
    }

    syncedPosterRef.current = {
      itemId: editingItem.id,
      posterFileId: editingItem.posterFileId ?? null,
      posterUrl: editingItem.posterUrl ?? null,
    };

    // Первый вход (sameItem=false) — базовая инициализация уже сделана в openEditItem,
    // здесь синкать не нужно, иначе затрём только что открытые данные.
    if (!sameItem) {
      return;
    }

    setEditItemData((current) => {
      if (!current) {return current;}
      return {
        ...current,
        posterUrl: editingItem.posterUrl ?? null,
        posterFileId: editingItem.posterFileId ?? null,
      };
    });

    // Если постер появился и мы были в состоянии extracting — гасим индикатор.
    if (editingItem.posterUrl && editingItem.posterFileId !== null) {
      setPosterState((p) =>
        p && p.itemId === editingItem.id && p.status === 'extracting' ? null : p,
      );
    }
  }, [editingItem, editItemData]);

  // Сбрасываем состояние автогенерации и авто-триггеров при закрытии sheet.
  useEffect(() => {
    if (editingItem) {return;}
    setPosterState(null);
    autoTriggeredItemsRef.current.clear();
    syncedPosterRef.current = null;
  }, [editingItem]);

  const isAddModuleOpen = addModuleOpen || internalAddModuleOpen;
  const setAddModuleOpen = onAddModuleOpenChange ?? setInternalAddModuleOpen;

  const createModuleMutation = useCreate();
  const createModule = createModuleMutation.mutate;
  const creatingModule = createModuleMutation.mutation.isPending;

  const { mutate: deleteModule } = useDelete();

  const createItemMutation = useCreate();
  const createItem = createItemMutation.mutate;
  const creatingItem = createItemMutation.mutation.isPending;

  const updateItemMutation = useUpdate();
  const updateItem = updateItemMutation.mutate;
  const updatingItem = updateItemMutation.mutation.isPending;

  const updateModuleMutation = useUpdate();
  const updateModule = updateModuleMutation.mutate;
  const updatingModule = updateModuleMutation.mutation.isPending;

  const { mutate: deleteItem } = useDelete();
  const { confirm, ConfirmationDialog } = useConfirm();

  const { mutate: reorderMutate } = useCustomMutation();

  // Локальная копия modules для оптимистичного drag-and-drop.
  // Пропы могут прийти в другом порядке до refetch — поэтому держим локальный массив.
  const [localModules, setLocalModules] = useState<Module[]>(modules);
  useEffect(() => {
    setLocalModules(modules);
  }, [modules]);

  const handleReorderModules = useCallback(
    (reordered: Module[]) => {
      const withOrder = reordered.map((m, i) => ({ ...m, order: i }));
      setLocalModules(withOrder);
      reorderMutate(
        {
          url: "/modules/reorder",
          method: "patch",
          values: { moduleIds: reordered.map((m) => m.id) },
        },
        {
          onSuccess: () => onRefresh(),
          onError: () => setLocalModules(modules),
        }
      );
    },
    [modules, onRefresh, reorderMutate]
  );

  const handleReorderItems = useCallback(
    (moduleId: string, reordered: ModuleItem[]) => {
      setLocalModules((prev) =>
        prev.map((m) =>
          m.id === moduleId
            ? { ...m, items: reordered.map((it, i) => ({ ...it, order: i })) }
            : m
        )
      );
      reorderMutate(
        {
          url: "/module-items/reorder",
          method: "patch",
          values: { itemIds: reordered.map((it) => it.id) },
        },
        {
          onSuccess: () => onRefresh(),
          onError: () => setLocalModules(modules),
        }
      );
    },
    [modules, onRefresh, reorderMutate]
  );

  const toggleExpand = (moduleId: string) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) {next.delete(moduleId);}
      else {next.add(moduleId);}
      return next;
    });
  };

  const currentLangMeta = allLanguages.find((l) => l.code === currentLang);

  const handleAddModule = () => {
    if (!newModuleTitle.trim()) {return;}
    createModule(
      {
        resource: "modules",
        values: {
          courseId,
          order: modules.length,
          translations: [
            {
              languageCode: currentLang,
              title: newModuleTitle.trim(),
              description: null,
            },
          ],
        },
      },
      {
        onSuccess: () => {
          setNewModuleTitle("");
          setAddModuleOpen(false);
          onRefresh();
        },
      }
    );
  };

  const handleDeleteModule = async (module: Module) => {
    if (
      !(await confirm({
        title: "Удалить модуль?",
        description: `Вы действительно хотите удалить модуль «${getModuleTitle(
          module,
          currentLang
        )}» и все его уроки? Это действие нельзя будет отменить.`,
        variant: "destructive",
      }))
    )
      {return;}
    deleteModule({ resource: "modules", id: module.id }, { onSuccess: onRefresh });
  };

  const handleAddItem = (moduleId: string) => {
    if (!newItemTitle.trim()) {return;}
    const module = modules.find((m) => m.id === moduleId);
    const createValues: Record<string, unknown> = {
      moduleId,
      type: newItemType,
      order: module?.items?.length ?? 0,
      translations: [
        { languageCode: currentLang, title: newItemTitle.trim(), content: null },
      ],
    };

    if (newItemType === 'HOMEWORK') {
      createValues.homeworkConfig = {
        homeworkType: 'ONE_TIME',
        deadline: null,
        streakDays: null,
        periodHours: null,
      };
    }

    createItem(
      {
        resource: "module-items",
        values: createValues,
      },
      {
        onSuccess: () => {
          setNewItemTitle("");
          setNewItemType("VIDEO");
          setAddingItemForModule(null);
          onRefresh();
        },
      }
    );
  };

  const handleDeleteItem = async (item: ModuleItem) => {
    if (
      !(await confirm({
        title: "Удалить урок?",
        description: `Вы действительно хотите удалить урок «${getItemTitle(
          item,
          currentLang
        )}»? Это действие нельзя будет отменить.`,
        variant: "destructive",
      }))
    )
      {return;}
    deleteItem({ resource: "module-items", id: item.id }, { onSuccess: onRefresh });
  };

  const openEditItem = (item: ModuleItem) => {
    const translations: Record<string, { title: string; content: string }> = {};
    for (const lang of allLanguages) {
      const t = (item.translations ?? []).find(
        (tr: ModuleItemTranslation) => tr.languageCode === lang.code
      );
      translations[lang.code] = {
        title: t?.title ?? "",
        content: t?.content ?? "",
      };
    }
    setEditItemData({
      translations,
      videoUrl: item.videoUrl ?? "",
      videoDuration: item.videoDuration,
      isPreview: item.isPreview,
      type: item.type,
      homeworkConfig: item.homeworkConfig ?? null,
      bookId: item.bookId ?? null,
      posterUrl: item.posterUrl ?? null,
      posterFileId: item.posterFileId ?? null,
      availableFrom: item.availableFrom ?? null,
    });
    // Фиксируем стартовые poster-значения, чтобы sync-эффект реагировал только
    // на реальные изменения со стороны сервера, а не на первичный рендер.
    syncedPosterRef.current = {
      itemId: item.id,
      posterFileId: item.posterFileId ?? null,
      posterUrl: item.posterUrl ?? null,
    };
    setEditingItem(item);
  };

  const openEditModule = (module: Module) => {
    const translations: Record<string, { title: string; description: string }> = {};
    for (const lang of allLanguages) {
      const t = (module.translations ?? []).find(
        (tr) => tr.languageCode === lang.code
      );
      translations[lang.code] = {
        title: t?.title ?? "",
        description: t?.description ?? "",
      };
    }
    setEditModuleData({ translations, availableFrom: module.availableFrom ?? null });
    setEditingModule(module);
  };

  const handleSaveItem = () => {
    if (!editingItem || !editItemData) {return;}

    const translationsArray = Object.entries(editItemData.translations)
      .filter(([, data]) => data.title.trim())
      .map(([langCode, data]) => ({
        languageCode: langCode,
        title: data.title.trim(),
        content: data.content.trim() || null,
      }));

    const values: Record<string, unknown> = {
      type: editItemData.type,
      isPreview: editItemData.isPreview,
      videoUrl: editItemData.videoUrl.trim() || null,
      videoDuration: editItemData.videoDuration,
      // bookId отправляем только для BOOK-уроков, иначе очищаем (смена типа).
      bookId: editItemData.type === ModuleItemTypeEnum.BOOK ? editItemData.bookId : null,
      // Постер — только для VIDEO. Для остальных типов очищаем.
      posterUrl: editItemData.type === ModuleItemTypeEnum.VIDEO ? editItemData.posterUrl : null,
      posterFileId: editItemData.type === ModuleItemTypeEnum.VIDEO ? editItemData.posterFileId : null,
      availableFrom: editItemData.availableFrom,
      translations: translationsArray,
    };

    if (editItemData.type === 'HOMEWORK' && editItemData.homeworkConfig) {
      values.homeworkConfig = {
        homeworkType: editItemData.homeworkConfig.homeworkType,
        deadline: editItemData.homeworkConfig.deadline || null,
        streakDays: editItemData.homeworkConfig.streakDays,
        periodHours: editItemData.homeworkConfig.periodHours,
      };
    }

    updateItem(
      {
        resource: "module-items",
        id: editingItem.id,
        values,
      },
      {
        onSuccess: () => {
          setEditingItem(null);
          setEditItemData(null);
          onRefresh();
        },
      }
    );
  };

  const handleSaveModule = () => {
    if (!editingModule || !editModuleData) {return;}

    const translationsArray = Object.entries(editModuleData.translations)
      .filter(([, data]) => data.title.trim())
      .map(([langCode, data]) => ({
        languageCode: langCode,
        title: data.title.trim(),
        description: data.description.trim() || null,
      }));

    updateModule(
      {
        resource: "modules",
        id: editingModule.id,
        values: {
          translations: translationsArray,
          availableFrom: editModuleData.availableFrom,
        },
      },
      {
        onSuccess: () => {
          setEditingModule(null);
          setEditModuleData(null);
          onRefresh();
        },
      }
    );
  };

  // Language tabs component to be rendered either inline or externally
  const languageTabsComponent = useMemo(() => (
    <LanguageScrollTabs
      languages={allLanguages}
      activeLanguage={currentLang}
      onLanguageChange={setCurrentLang}
      fillStatus={fillStatus}
      visibleLanguages={visibleLanguages}
      onVisibleLanguagesChange={handleVisibleLanguagesChange}
      showLanguageSelector
      showTabs={false}
      size="sm"
    />
  ), [allLanguages, currentLang, fillStatus, visibleLanguages, handleVisibleLanguagesChange]);

  return (
    <>
      {ConfirmationDialog}

      {/* Language Tabs */}
      {allLanguages.length > 0 && (
        <div className="mb-4">
          {languageTabsComponent}
        </div>
      )}

      {/* Overall fill summary */}
      {allLanguages.length > 0 && modules.length > 0 && (
        <div className="flex items-center justify-between mb-4 p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Всего:</span>
            <span className="font-medium text-foreground">{modules.length} модулей</span>
            <span>•</span>
            <span className="font-medium text-foreground">
              {modules.reduce((acc, m) => acc + (m.items?.length ?? 0), 0)} уроков
            </span>
          </div>
          <LanguageFillSummary fillStatus={fillStatus} languages={allLanguages} />
        </div>
      )}

      {/* Modules List */}
      {localModules.length === 0 ? (
        <div className="text-center text-muted-foreground py-12 border rounded-lg bg-muted/20">
          <p>Нет модулей. Добавьте первый модуль.</p>
        </div>
      ) : (
        <SortableList
          items={[...localModules].sort((a, b) => a.order - b.order)}
          onReorder={handleReorderModules}
          renderItem={({ item: module, dragHandleProps }) => (
              <div className="border rounded-lg">
                {/* Module Header */}
                <div className="flex items-center gap-2 p-3 bg-muted/50">
                  <div {...dragHandleProps} className="flex items-center">
                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-0 h-auto"
                    onClick={() => toggleExpand(module.id)}
                  >
                    {expandedModules.has(module.id) ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                  <span className="font-medium flex-1">
                    {getModuleTitle(module, currentLang)}
                  </span>
                  <ModuleFillIndicator
                    module={module}
                    languages={allLanguages}
                  />
                  <Badge variant="secondary">
                    {(module.items ?? []).length} уроков
                  </Badge>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => openEditModule(module)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleDeleteModule(module)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>

                {/* Module Items */}
                {expandedModules.has(module.id) && (
                  <div className="p-3 pt-0">
                    {(module.items ?? []).length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 text-center">
                        Нет уроков в этом модуле
                      </p>
                    ) : (
                      <SortableList
                        className="space-y-1 mt-2"
                        items={[...(module.items ?? [])].sort((a, b) => a.order - b.order)}
                        onReorder={(reordered) => handleReorderItems(module.id, reordered)}
                        renderItem={({ item, dragHandleProps: itemDragHandleProps }) => (
                            <div
                              role="button"
                              tabIndex={0}
                              className="flex items-center gap-2 p-2 rounded border bg-background hover:bg-muted/30 cursor-pointer group"
                              onClick={() => openEditItem(item)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  openEditItem(item);
                                }
                              }}
                            >
                              <div
                                {...itemDragHandleProps}
                                role="button"
                                tabIndex={-1}
                                aria-label="Перетащить урок"
                                onClick={(e) => e.stopPropagation()}
                                className="flex items-center"
                              >
                                <GripVertical className="h-3 w-3 text-muted-foreground" />
                              </div>
                              {getItemIcon(item.type)}
                              <span className="flex-1 text-sm">
                                {getItemTitle(item, currentLang)}
                              </span>
                              <ItemFillIndicator
                                item={item}
                                languages={allLanguages}
                              />
                              <Badge variant="outline" className="text-xs">
                                {MODULE_ITEM_TYPE_OPTIONS.find((o) => o.value === item.type)
                                  ?.label ?? item.type}
                              </Badge>
                              {item.isPreview && (
                                <Badge variant="secondary" className="text-xs">
                                  Превью
                                </Badge>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 opacity-0 group-hover:opacity-100"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditItem(item);
                                }}
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteItem(item);
                                }}
                              >
                                <Trash2 className="h-3 w-3 text-destructive" />
                              </Button>
                            </div>
                          )}
                      />
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2 w-full"
                      onClick={() => setAddingItemForModule(module.id)}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Добавить урок
                    </Button>
                  </div>
                )}
              </div>
          )}
        />
      )}

      {/* Add Module Dialog */}
      <Dialog open={isAddModuleOpen} onOpenChange={setAddModuleOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Добавить модуль</DialogTitle>
            <DialogDescription>
              Создайте новый модуль для курса. Название на языке: {currentLangMeta?.nativeName ?? currentLang}
            </DialogDescription>
          </DialogHeader>
          <Input
            value={newModuleTitle}
            onChange={(e) => setNewModuleTitle(e.target.value)}
            placeholder="Название модуля"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddModuleOpen(false)}>
              Отмена
            </Button>
            <Button
              onClick={handleAddModule}
              disabled={creatingModule || !newModuleTitle.trim()}
            >
              {creatingModule && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Добавить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Item Dialog */}
      <Dialog
        open={!!addingItemForModule}
        onOpenChange={() => setAddingItemForModule(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Добавить урок</DialogTitle>
            <DialogDescription>
              Создайте новый урок. Название на языке: {currentLangMeta?.nativeName ?? currentLang}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              value={newItemTitle}
              onChange={(e) => setNewItemTitle(e.target.value)}
              placeholder="Название урока"
            />
            <div className="flex flex-wrap gap-2">
              {MODULE_ITEM_TYPE_OPTIONS.map((opt) => (
                <Button
                  key={opt.value}
                  variant={newItemType === opt.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setNewItemType(opt.value)}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddingItemForModule(null)}>
              Отмена
            </Button>
            <Button
              onClick={() => addingItemForModule && handleAddItem(addingItemForModule)}
              disabled={creatingItem || !newItemTitle.trim()}
            >
              {creatingItem && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Добавить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Item Sheet */}
      <Sheet open={!!editingItem} onOpenChange={(open) => { if (!open) { setEditingItem(null); } }}>
        <SheetContent className="sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Редактировать урок</SheetTitle>
            <SheetDescription>
              Заполните информацию об уроке на разных языках
            </SheetDescription>
          </SheetHeader>

          {editItemData && editingItem && (
            <div className="space-y-6">
              {/* Type & Preview */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Тип урока</Label>
                  <Select
                    value={editItemData.type}
                    onValueChange={(v) => {
                      const newType = v as ModuleItemType;
                      setEditItemData({
                        ...editItemData,
                        type: newType,
                        homeworkConfig: newType === 'HOMEWORK'
                          ? (editItemData.homeworkConfig ?? { homeworkType: 'ONE_TIME', deadline: null, streakDays: null, periodHours: null })
                          : editItemData.homeworkConfig,
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MODULE_ITEM_TYPE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {hasPricingTiers && (
                  <div className="space-y-2">
                    <Label>Доступ</Label>
                    <div className="flex items-center gap-2 h-10">
                      <Switch
                        checked={editItemData.isPreview}
                        onCheckedChange={(checked) =>
                          setEditItemData({ ...editItemData, isPreview: checked })
                        }
                      />
                      <span className="text-sm">Доступен без покупки</span>
                    </div>
                  </div>
                )}
              </div>

              {/* BOOK: выбор книги из библиотеки */}
              {editItemData.type === ModuleItemTypeEnum.BOOK && (
                <div className="space-y-2">
                  <Label>Книга из библиотеки</Label>
                  <BookSelector
                    value={editItemData.bookId}
                    onChange={(bookId) =>
                      setEditItemData({ ...editItemData, bookId })
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    Можно выбрать как опубликованную книгу, так и черновик —
                    пользователи увидят её, когда она будет опубликована.
                  </p>
                </div>
              )}

              {/* AUDIO: файл прикрепляется через ModuleItemAttachments (mediaType=AUDIO).
                  Секция ниже (единая для всех типов) уже это позволяет. */}
              {editItemData.type === ModuleItemTypeEnum.AUDIO && (
                <div className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Headphones className="h-4 w-4" />
                    <span>
                      Загрузите аудиофайл в секции «Прикреплённые файлы» ниже —
                      он будет проигрываться в уроке.
                    </span>
                  </div>
                </div>
              )}

              {/* Video Upload (for VIDEO type) */}
              {editItemData.type === ModuleItemTypeEnum.VIDEO && (() => {
                const videoUrlChanged = editItemData.videoUrl !== (editingItem.videoUrl ?? '');
                return (
                  <div className="space-y-2">
                    <Label>Видео</Label>
                    <LivestreamSourceBadge moduleItemId={Number(editingItem.id)} />
                    <VideoUpload
                      value={editItemData.videoUrl}
                      onChange={(url) =>
                        setEditItemData({ ...editItemData, videoUrl: url })
                      }
                      moduleItemId={editingItem.id}
                      processingStatus={videoUrlChanged ? null : editingItem.processingStatus}
                      processingError={videoUrlChanged ? null : editingItem.processingError}
                      hlsPlaylistUrl={videoUrlChanged ? null : editingItem.hlsPlaylistUrl}
                      isProtected={videoUrlChanged ? false : editingItem.isProtected}
                      onProcessingStatusChange={() => onRefresh()}
                    />
                  </div>
                );
              })()}

              {/* Постер урока (только для VIDEO) */}
              {editItemData.type === ModuleItemTypeEnum.VIDEO && (
                <div className="space-y-2">
                  <Label>Постер урока</Label>
                  <FileImageUpload
                    value={
                      editItemData.posterFileId !== null && editItemData.posterUrl
                        ? { id: editItemData.posterFileId, url: editItemData.posterUrl }
                        : null
                    }
                    onChange={(file) => {
                      setEditItemData({
                        ...editItemData,
                        posterUrl: file?.url ?? null,
                        posterFileId: file?.id ?? null,
                      });
                      if (file && editingItem) {
                        setPosterState((prev) =>
                          prev && prev.itemId === editingItem.id ? null : prev,
                        );
                      }
                    }}
                    placeholder="Перетащите постер или нажмите для выбора"
                  />
                  {posterExtracting && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      <span>Автогенерация постера из видео…</span>
                    </div>
                  )}
                  {posterExtractFailed && !posterExtracting && (
                    <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400">
                      <AlertCircle className="h-3 w-3" />
                      <span>Автогенерация не удалась, загрузите постер вручную.</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">
                      Постер для секции «Продолжить просмотр». Если не задан — будет использован автоматически извлечённый кадр видео или обложка курса.
                    </p>
                    {(editingItem.hlsPlaylistUrl || editingItem.videoUrl) && (
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap ml-2"
                        disabled={posterExtracting}
                        onClick={() => { void triggerExtractPoster(editingItem.id); }}
                      >
                        Перегенерировать постер из видео
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Homework Config (for HOMEWORK type) */}
              {editItemData.type === "HOMEWORK" && (
                <div className="space-y-4 rounded-lg border p-4">
                  <Label className="text-base font-semibold">Настройки домашнего задания</Label>

                  <div className="space-y-2">
                    <Label>Тип домашнего задания</Label>
                    <Select
                      value={editItemData.homeworkConfig?.homeworkType ?? "ONE_TIME"}
                      onValueChange={(v) =>
                        setEditItemData({
                          ...editItemData,
                          homeworkConfig: {
                            homeworkType: v as HomeworkType,
                            deadline: editItemData.homeworkConfig?.deadline ?? null,
                            streakDays: v === 'DAILY_STREAK' ? (editItemData.homeworkConfig?.streakDays ?? 7) : null,
                            periodHours: v === 'PERIOD' ? (editItemData.homeworkConfig?.periodHours ?? 24) : null,
                          },
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ONE_TIME">
                          <div className="flex items-center gap-2">
                            <CalendarClock className="h-4 w-4" />
                            Разовое задание
                          </div>
                        </SelectItem>
                        <SelectItem value="DAILY_STREAK">
                          <div className="flex items-center gap-2">
                            <Flame className="h-4 w-4" />
                            Ежедневный стрик
                          </div>
                        </SelectItem>
                        <SelectItem value="PERIOD">
                          <div className="flex items-center gap-2">
                            <Timer className="h-4 w-4" />
                            Периодическое
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Дедлайн</Label>
                    <DateTimePicker
                      value={editItemData.homeworkConfig?.deadline
                        ? new Date(editItemData.homeworkConfig.deadline)
                        : null}
                      onChange={(date) =>
                        setEditItemData({
                          ...editItemData,
                          homeworkConfig: {
                            ...(editItemData.homeworkConfig ?? { homeworkType: 'ONE_TIME' as HomeworkType, streakDays: null, periodHours: null }),
                            deadline: date ? date.toISOString() : null,
                          },
                        })
                      }
                      placeholder="Выберите дедлайн"
                      disablePast
                    />
                    <p className="text-xs text-muted-foreground">
                      Необязательно. Если указан — ДЗ должно быть сдано до этой даты.
                    </p>
                  </div>

                  {editItemData.homeworkConfig?.homeworkType === 'DAILY_STREAK' && (
                    <div className="space-y-2">
                      <Label>Количество дней стрика</Label>
                      <Input
                        type="number"
                        min={1}
                        value={editItemData.homeworkConfig.streakDays ?? ""}
                        onChange={(e) =>
                          setEditItemData({
                            ...editItemData,
                            homeworkConfig: editItemData.homeworkConfig
                              ? { ...editItemData.homeworkConfig, streakDays: e.target.value ? Number(e.target.value) : null }
                              : null,
                          })
                        }
                        placeholder="7"
                      />
                      <p className="text-xs text-muted-foreground">
                        Сколько дней подряд нужно выполнять задание.
                      </p>
                    </div>
                  )}

                  {editItemData.homeworkConfig?.homeworkType === 'PERIOD' && (
                    <div className="space-y-2">
                      <Label>Период (часы)</Label>
                      <Input
                        type="number"
                        min={1}
                        value={editItemData.homeworkConfig.periodHours ?? ""}
                        onChange={(e) =>
                          setEditItemData({
                            ...editItemData,
                            homeworkConfig: editItemData.homeworkConfig
                              ? { ...editItemData.homeworkConfig, periodHours: e.target.value ? Number(e.target.value) : null }
                              : null,
                          })
                        }
                        placeholder="24"
                      />
                      <p className="text-xs text-muted-foreground">
                        Через сколько часов можно сдать задание повторно.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Translations Tabs */}
              <Tabs defaultValue={allLanguages[0]?.code ?? "ru"} className="w-full">
                <TabsList className="w-full justify-start flex-wrap h-auto gap-1">
                  {allLanguages.map((lang) => {
                    const data = editItemData.translations[lang.code];
                    const hasTitle = data?.title?.trim();
                    const hasContent = data?.content?.trim();
                    const isComplete = hasTitle && (editItemData.type !== "ARTICLE" || hasContent);
                    const isPartial = hasTitle || hasContent;

                    return (
                      <TabsTrigger
                        key={lang.code}
                        value={lang.code}
                        className="gap-1.5"
                      >
                        <span>{lang.flag || lang.flagEmoji}</span>
                        <span>{lang.nativeName}</span>
                        {isComplete ? (
                          <Check className="h-3 w-3 text-green-500" />
                        ) : (isPartial ? (
                          <AlertCircle className="h-3 w-3 text-amber-500" />
                        ) : (
                          <div className="h-2 w-2 rounded-full bg-muted-foreground/30" />
                        ))}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>

                {allLanguages.map((lang) => (
                  <TabsContent key={lang.code} value={lang.code} forceMount className="space-y-4 mt-4 data-[state=inactive]:hidden">
                    <div className="space-y-2">
                      <Label>Название ({lang.nativeName})</Label>
                      <Input
                        value={editItemData.translations[lang.code]?.title ?? ""}
                        onChange={(e) =>
                          setEditItemData({
                            ...editItemData,
                            translations: {
                              ...editItemData.translations,
                              [lang.code]: {
                                ...editItemData.translations[lang.code],
                                title: e.target.value,
                              },
                            },
                          })
                        }
                        placeholder="Название урока"
                      />
                    </div>

                    {/* Content editor for ARTICLE and HOMEWORK */}
                    {(editItemData.type === "ARTICLE" ||
                      editItemData.type === "HOMEWORK") && (
                      <div className="space-y-2">
                        <Label>
                          {editItemData.type === "ARTICLE" ? "Содержимое статьи" : "Описание задания"} ({lang.nativeName})
                        </Label>
                        <RichTextEditor
                          value={editItemData.translations[lang.code]?.content ?? ""}
                          onChange={(html) =>
                            setEditItemData({
                              ...editItemData,
                              translations: {
                                ...editItemData.translations,
                                [lang.code]: {
                                  ...editItemData.translations[lang.code],
                                  content: html,
                                },
                              },
                            })
                          }
                          placeholder={
                            editItemData.type === "ARTICLE"
                              ? "Напишите содержимое статьи..."
                              : "Опишите задание для студентов..."
                          }
                        />
                      </div>
                    )}

                    {/* Description for VIDEO */}
                    {editItemData.type === "VIDEO" && (
                      <div className="space-y-2">
                        <Label>Описание видео ({lang.nativeName})</Label>
                        <RichTextEditor
                          value={editItemData.translations[lang.code]?.content ?? ""}
                          onChange={(html) =>
                            setEditItemData({
                              ...editItemData,
                              translations: {
                                ...editItemData.translations,
                                [lang.code]: {
                                  ...editItemData.translations[lang.code],
                                  content: html,
                                },
                              },
                            })
                          }
                          placeholder="Дополнительное описание к видео (необязательно)..."
                        />
                      </div>
                    )}
                  </TabsContent>
                ))}
              </Tabs>

              <div className="space-y-2 border-t pt-4">
                <Label>Доступно с</Label>
                <DateTimePicker
                  value={editItemData.availableFrom ? new Date(editItemData.availableFrom) : null}
                  onChange={(date) =>
                    setEditItemData({
                      ...editItemData,
                      availableFrom: date ? date.toISOString() : null,
                    })
                  }
                  placeholder="Не задано"
                />
                <p className="text-xs text-muted-foreground">
                  Если задано — урок откроется ученику в эту дату, даже при
                  наличии доступа к курсу. Перебивает дату модуля. Пусто =
                  наследуется от модуля (или доступен сразу).
                </p>
              </div>

              {/* Локализация медиа (субтитры, аудио, видео) */}
              <ModuleItemMediaSection moduleItemId={editingItem.id} />

              {/*
                Прикреплённые к уроку файлы (PDF/DOC/изображения и пр.)
                Доступны для любого типа урока (VIDEO/ARTICLE/HOMEWORK),
                выдаются ученикам только при наличии доступа к уроку
                (или бесплатно, если isPreview=true).
              */}
              <ModuleItemAttachments moduleItemId={editingItem.id} />

              <div className="space-y-2 border-t pt-4">
                <Label>Аудитория (теги)</Label>
                <ContentTagsField
                  entityType="MODULE_ITEM"
                  entityId={Number(editingItem.id)}
                  label=""
                  description="Урок виден только пользователям с пересечением тегов. Пусто = виден всем."
                />
              </div>
            </div>
          )}

          <SheetFooter className="mt-6 px-4">
            <Button variant="outline" onClick={() => setEditingItem(null)}>
              Отмена
            </Button>
            <Button onClick={handleSaveItem} disabled={updatingItem}>
              {updatingItem && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Сохранить
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Edit Module Sheet */}
      <Sheet open={!!editingModule} onOpenChange={(open) => { if (!open) { setEditingModule(null); } }}>
        <SheetContent className="sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Редактировать модуль</SheetTitle>
            <SheetDescription>
              Заполните название и описание модуля на разных языках
            </SheetDescription>
          </SheetHeader>

          {editModuleData && editingModule && (
            <div className="space-y-6">
              {/* Translations Tabs */}
              <Tabs defaultValue={allLanguages[0]?.code ?? "ru"} className="w-full">
                <TabsList className="w-full justify-start flex-wrap h-auto gap-1">
                  {allLanguages.map((lang) => {
                    const data = editModuleData.translations[lang.code];
                    const hasTitle = data?.title?.trim();
                    const hasDescription = data?.description?.trim();
                    const isComplete = hasTitle;
                    const isPartial = hasTitle || hasDescription;

                    return (
                      <TabsTrigger
                        key={lang.code}
                        value={lang.code}
                        className="gap-1.5"
                      >
                        <span>{lang.flag || lang.flagEmoji}</span>
                        <span>{lang.nativeName}</span>
                        {isComplete ? (
                          <Check className="h-3 w-3 text-green-500" />
                        ) : (isPartial ? (
                          <AlertCircle className="h-3 w-3 text-amber-500" />
                        ) : (
                          <div className="h-2 w-2 rounded-full bg-muted-foreground/30" />
                        ))}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>

                {allLanguages.map((lang) => (
                  <TabsContent key={lang.code} value={lang.code} forceMount className="space-y-4 mt-4 data-[state=inactive]:hidden">
                    <div className="space-y-2">
                      <Label>Название модуля ({lang.nativeName}) *</Label>
                      <Input
                        value={editModuleData.translations[lang.code]?.title ?? ""}
                        onChange={(e) =>
                          setEditModuleData({
                            ...editModuleData,
                            translations: {
                              ...editModuleData.translations,
                              [lang.code]: {
                                ...editModuleData.translations[lang.code],
                                title: e.target.value,
                              },
                            },
                          })
                        }
                        placeholder="Название модуля"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Краткое описание ({lang.nativeName})</Label>
                      <Input
                        value={editModuleData.translations[lang.code]?.description ?? ""}
                        onChange={(e) =>
                          setEditModuleData({
                            ...editModuleData,
                            translations: {
                              ...editModuleData.translations,
                              [lang.code]: {
                                ...editModuleData.translations[lang.code],
                                description: e.target.value,
                              },
                            },
                          })
                        }
                        placeholder="Краткое описание модуля (необязательно)"
                      />
                      <p className="text-xs text-muted-foreground">
                        Отображается в списке модулей под названием
                      </p>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>

              <div className="space-y-2 border-t pt-4">
                <Label>Доступно с</Label>
                <DateTimePicker
                  value={editModuleData.availableFrom ? new Date(editModuleData.availableFrom) : null}
                  onChange={(date) =>
                    setEditModuleData({
                      ...editModuleData,
                      availableFrom: date ? date.toISOString() : null,
                    })
                  }
                  placeholder="Не задано"
                />
                <p className="text-xs text-muted-foreground">
                  Дата и время, когда модуль и его уроки откроются ученикам.
                  Пусто = доступен сразу при наличии доступа к курсу.
                </p>
              </div>

              <div className="space-y-2 border-t pt-4">
                <Label>Аудитория (теги)</Label>
                <ContentTagsField
                  entityType="MODULE"
                  entityId={Number(editingModule.id)}
                  label=""
                  description="Модуль виден только пользователям с пересечением тегов. Пусто = виден всем."
                />
              </div>
            </div>
          )}

          <SheetFooter className="mt-6 px-4">
            <Button variant="outline" onClick={() => setEditingModule(null)}>
              Отмена
            </Button>
            <Button onClick={handleSaveModule} disabled={updatingModule}>
              {updatingModule && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Сохранить
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
